import { Component, OnInit, Input, Output, EventEmitter, signal, WritableSignal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';

import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { PeoplesService } from '../services/peoples.service';
import { TeamsService } from '../services/teams.service';
import { People, PeopleAdapter } from '../models/people';
import { Team } from '../models/team';
import { ITeamAllocation } from '../_interfaces/teamAllocation';
import { IAssignation } from '../_interfaces/Assignation';
import { TranslatePipe } from '@ngx-translate/core';
import { hasDuplicateTeamAssignationValidator, startDateAfterHiringDateValidator } from './team-assignation-modal.validators';
import {dateOrderValidator, isDateValidValidator} from '../_utils/validators/dateValidator';
import { getFormattedDate } from '../_utils/dateFormatter';
import { ToastService } from '../services/toast.service';


@Component({
  selector: 'app-team-assignation-modal',
  templateUrl: './team-assignation-modal.component.html',
  styleUrls: ['./team-assignation-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe
  ],
  providers: [DatePipe]
})
export class TeamAssignationModalComponent implements OnInit {
  @Input() people: People | null = null;
  @Input() contextTeamId: number | null = null;

  @Output() modalClosed = new EventEmitter<ITeamAllocation[] | undefined>();

  assignationForm!: FormGroup;
  allTeams: Team[] = [];
  selectedPersonInternal: People | null = null; // Stocke l'objet People complet sur lequel on travaille

  public totalAllocationRate: WritableSignal<number> = signal(0);
  public availablePeople: People[] = []; // Liste des personnes disponibles pour le dropdown (si affiché)
  public isLoadingPeople: boolean = false;

  constructor(
    private fb: FormBuilder,
    private peoplesService: PeoplesService,
    private teamsService: TeamsService,
    private peopleAdapter: PeopleAdapter,
    private toastService: ToastService,
    private datePipe: DatePipe
  ) {
    this.assignationForm = this.fb.group({
      selectedPersonId: new FormControl(null, [Validators.required]),
      // teamAssignations: this.fb.array([], [hasDuplicateTeamAssignationValidator()]),
      teamAssignations: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    this.fetchAllTeams();
    this.fetchAllPeople();
    this.assignments.valueChanges.subscribe(() => this.calculateTotalAllocation());

    if (this.people) {
      this.selectedPersonInternal = this.people;
      this.assignationForm.get('selectedPersonId')?.setValue(this.people.id);
      this.assignationForm.get('selectedPersonId')?.disable();
      this.initializeForm(this.selectedPersonInternal.teams);
    } else {
      this.selectedPersonInternal = null;
      this.assignationForm.get('selectedPersonId')?.enable();
      this.initializeForm([]);
    }
  }

  get assignments(): FormArray {
    return this.assignationForm.get('teamAssignations') as FormArray;
  }

  public calculateTotalAllocation(): void {
    const total = this.assignments.controls
      .map(control => control.get('allocationRate')?.value || 0)
      .reduce((sum, allocationRate) => sum + (typeof allocationRate === 'number' ? allocationRate : parseInt(allocationRate, 10)), 0);
    this.totalAllocationRate.set(total);
  }

  public addAssignment(teamId: number = 0, allocationRate: number = 0, startDate: string = '', endDate: string | null = null, disableTeamField: boolean = false): void {
    
    const initialStartDate = startDate || getFormattedDate(new Date());
    
    const newAssignmentGroup = this.fb.group({
      team_id: [{ value: teamId, disabled: disableTeamField }, Validators.required],
      allocationRate: [allocationRate, [Validators.required, Validators.min(0), Validators.max(100)]],
      startDate: [initialStartDate, [Validators.required, isDateValidValidator, startDateAfterHiringDateValidator(this.people?.hiring_date)]],
      endDate: [endDate, [isDateValidValidator] ]
    }, { validators: dateOrderValidator('startDate', 'endDate') });
    this.assignments.push(newAssignmentGroup);
    this.calculateTotalAllocation();
  }

  public removeAssignment(index: number): void {
    this.assignments.removeAt(index);
    this.calculateTotalAllocation();
  }

  private initializeForm(existingAllocations: ITeamAllocation[]): void {
    this.assignments.clear();

    const isExistingPersonSelected = !!this.selectedPersonInternal;

    let existingContextAllocation: ITeamAllocation | undefined;

    if (this.contextTeamId !== null) {
      existingContextAllocation = existingAllocations.find(ta => ta.team.id === Number(this.contextTeamId));
    }

    if (this.contextTeamId !== null) {
      if (existingContextAllocation) {
        this.addAssignment(existingContextAllocation.team.id, existingContextAllocation.allocation_rate, existingContextAllocation.startDate, existingContextAllocation.endDate, false);
      } else if (isExistingPersonSelected && !existingContextAllocation) {
        this.addAssignment(Number(this.contextTeamId), 0, '', null, true);
      } else if (!isExistingPersonSelected) {
        this.addAssignment(Number(this.contextTeamId), 0, '', null, true);
      }
    }

    existingAllocations.forEach(teamAllocation => {
      const currentContextTeamIdNum = this.contextTeamId !== null ? Number(this.contextTeamId) : null;
      if (currentContextTeamIdNum === null || teamAllocation.team.id !== currentContextTeamIdNum) {
        this.addAssignment(teamAllocation.team.id, teamAllocation.allocation_rate, teamAllocation.startDate, teamAllocation.endDate, false);
      }
    });

    if (this.assignments.controls.length === 0) {
      this.addAssignment();
    }

    this.calculateTotalAllocation();
  }

  loadSelectedPerson(personId: number): void {
    this.peoplesService.get_people(personId).pipe(
      map((data: any) => this.peopleAdapter.adapt(data)),
      catchError(error => {
        console.error('[Modal] Error loading person:', error);
        this.selectedPersonInternal = null;
        this.initializeForm([]);
        this.assignationForm.get('selectedPersonId')?.reset();
        return of(null);
      })
    ).subscribe({
      next: (personData: People | null) => {
        if (personData) {
          this.selectedPersonInternal = personData;
          this.initializeForm(this.selectedPersonInternal.teams);
          this.assignationForm.get('selectedPersonId')?.setValue(personData.id);
        }
      },
    });
  }

  fetchAllTeams(): void {
    this.teamsService.getTeams().subscribe(
      (teams: Team[]) => {
        this.allTeams = teams;
      },
      error => {
        console.error('[Modal] Error fetching teams:', error);
        this.allTeams = [];
      }
    );
  }

  fetchAllPeople(): void {
    this.isLoadingPeople = true;
    this.peoplesService.get_peoples(null).pipe(
      map((data: any[]) => {
          return data.map(item => this.peopleAdapter.adapt(item));
      }),
      catchError((error: any) => {
        console.error('[Modal] Error fetching people:', error);
        this.availablePeople = [];
        this.isLoadingPeople = false;
        return of([]);
      })
    ).subscribe({
      next: (adaptedPeopleList: People[]) => {
        this.availablePeople = adaptedPeopleList;
        this.isLoadingPeople = false;
      },
    });
  }

  onPersonSelected(): void {
    const selectedId = this.assignationForm.get('selectedPersonId')?.value;
    if (selectedId) {
      this.loadSelectedPerson(selectedId);
    } else {
      this.selectedPersonInternal = null;
      this.assignationForm.get('selectedPersonId')?.enable();
      this.initializeForm([]);
    }
  }

  hasDuplicateTeamAssignation(): boolean {
    const selectedTeamIds = this.assignments.controls
      .filter(ctrl => ctrl.get('team_id')?.enabled)
      .map(ctrl => ctrl.get('team_id')?.value)
      .filter(teamId => teamId !== null && teamId !== undefined && teamId !== '')
      .map(teamId => String(teamId));

    const uniqueTeamIds = new Set();
    for (const teamId of selectedTeamIds) {
      if (uniqueTeamIds.has(teamId)) {
        return true;
      }
      uniqueTeamIds.add(teamId);
    }
    return false;
  }


  hasMissingTeam(): boolean {
    // check if any assignment has a team_id that is either null, undefined, or 0
    return this.assignments.controls.some(ctrl =>
      (!ctrl.get('team_id')?.value || ctrl.get('team_id')?.value === 0) && ctrl.get('team_id')?.enabled
    );
  }

  
  isPersonSelectionRequiredAndMissing(): boolean {
    //if people has been provided, so no need to select someone in select dropdown
    if (this.people) {
      return false;
    }
    const selectedPersonIdControl = this.assignationForm.get('selectedPersonId');
    return (selectedPersonIdControl?.invalid ?? false) && (selectedPersonIdControl?.enabled ?? true);
  }

  hasInvalidAllocationRate(): boolean {
    return this.assignments.controls.some(ctrl => {
      const val = ctrl.get('allocationRate')?.value;
      return val === null || val === undefined || val === '' || isNaN(val) || parseInt(val, 10) <= 0 || parseInt(val, 10) > 100;
    });
  }

  hasAssignationError(): boolean {
    return (
      this.totalAllocationRate() > 100 ||
      this.hasDuplicateTeamAssignation() ||
      // this.assignments.hasError('duplicateTeam') ||
      this.hasMissingTeam() ||
      this.hasInvalidAllocationRate() ||
      this.isPersonSelectionRequiredAndMissing()
    );
  }

  public onSubmit(): void {
    const selectedPersonIdControl = this.assignationForm.get('selectedPersonId');
    const teamIdControls = this.assignments.controls.map(ctrl => ctrl.get('team_id'));

    // Temporairement réactiver les champs qui pourraient être désactivés (ex: team_id si `contextTeamId` est fourni,
    // ou `selectedPersonId` si on était en mode édition et qu'il était désactivé initialement).
    if (selectedPersonIdControl?.disabled) {
      selectedPersonIdControl.enable();
    }
    teamIdControls.forEach(ctrl => {
      if (ctrl?.disabled) {
        ctrl.enable();
      }
    });

    this.assignationForm.updateValueAndValidity();

    if (this.assignationForm.invalid || this.hasAssignationError()) {
        console.error('[Modal] Form is invalid or has assignation errors. Cannot save.');
        this.assignationForm.markAllAsTouched();
        // Re-désactiver les champs si ils l'étaient avant la soumission.
        // Important: `selectedPersonIdControl` est seulement désactivé si `this.people` est fourni.
        // Sinon, il reste activé car l'utilisateur peut le changer.
        if (this.people && selectedPersonIdControl?.enabled === false) { // Re-désactiver si mode édition
            selectedPersonIdControl.disable();
        }
        teamIdControls.forEach(ctrl => { // Re-désactiver les champs d'équipe spécifiques
            if (ctrl?.enabled === false) {
                ctrl.disable();
            }
        });
        return;
    }

    // Cette vérification `!this.selectedPersonInternal` ici est une sécurité supplémentaire.
    // En théorie, `isPersonSelectionRequiredAndMissing()` devrait déjà couvrir ce cas si `Validators.required`
    // est bien appliqué et que l'utilisateur est censé choisir une personne.
    if (!this.selectedPersonInternal) {
        console.error('[Modal] No person selected for assignment. Cannot save.');
        // Re-désactiver les champs comme ci-dessus en cas d'erreur précoce
        if (this.people && selectedPersonIdControl?.enabled === false) {
            selectedPersonIdControl.disable();
        }
        teamIdControls.forEach(ctrl => {
            if (ctrl?.enabled === false) {
                ctrl.disable();
            }
        });
        return;
    }

    const payload: IAssignation[] = this.assignments.controls.map((control: any) => {
      const rawValue = control.getRawValue();
      return {
          team: rawValue.team_id,
          allocation_rate: rawValue.allocationRate,
          start_date: rawValue.startDate,
          end_date: rawValue.endDate|| null
      };
    });

    this.peoplesService.update_people_assignments(this.selectedPersonInternal, payload).subscribe(
        (updatedTeams: ITeamAllocation[]) => {
            this.modalClosed.emit(updatedTeams);
            this.resetFormAndFields();
            this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
        },
        (error) => {
            console.error('[Modal] Error updating assignments:', error);
            // En cas d'erreur de soumission, restaurer l'état des contrôles.
            if (this.people && selectedPersonIdControl?.enabled === false) {
                selectedPersonIdControl.disable();
            }
            teamIdControls.forEach(ctrl => {
                if (ctrl?.enabled === false) {
                    ctrl.disable();
                }
            });
            this.toastService.showSuccess('COMMON.UPDATE_ERRO');
        }
    );
  }

  public onClose(): void {
    this.modalClosed.emit(undefined);
    this.resetFormAndFields();
  }

  private resetFormAndFields(): void {
    this.assignationForm.reset();
    this.assignments.clear();
    // Toujours activer le contrôle selectedPersonId lors du reset
    // pour qu'il soit prêt pour la prochaine ouverture de la modale.
    this.assignationForm.get('selectedPersonId')?.enable();
    this.selectedPersonInternal = null;
  }

  trackByPersonId(index: number, person: People): number {
    return person.id;
  }
}